import { ApolloError } from '@apollo/client';
import { Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { Button } from '@/components/form/baseInputs/Button';
import { TextInput } from '@/components/form/baseInputs/TextInput';
import { DialogModal } from '@/components/modals/DialogModal/DialogModal';
import { Alert } from '@/components/notifications/Alert/Alert';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { getGraphQLValidationIssues } from '@/graphql/errors';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useReportError } from '@/hooks/useReportError';
import { downloadJSON } from '@/modules/files/utils/fileUtils';
import { useFormDraftImportContractQuery } from './AdminFormsPage.generated';
const importErrorMessage = 'JSON could not be applied. Check your connection and permissions, then try again.';
interface FormJsonImportModalProps {
  mode: 'create' | 'update';
  onClose: () => void;
  onApply: (json: string) => Promise<void>;
}
export function FormJsonImportModal({
  mode,
  onClose,
  onApply
}: FormJsonImportModalProps) {
  const [json, setJson] = useState('');
  const [isApplying, setIsApplying] = useState(false);
  const [messages, setMessages] = useState<string[]>([]);
  const {
    data,
    loading,
    error,
    refetch
  } = useFormDraftImportContractQuery();
  const contract = data?.formDraftImportContract;
  const [copy] = useCopyToClipboard();
  const {
    showFeedback
  } = useFeedback();
  const {
    reportError
  } = useReportError();
  useReportError('Failed to load form import contract', error);
  const apply = async () => {
    setMessages([]);
    setIsApplying(true);
    try {
      await onApply(json);
      onClose();
    } catch (applyError) {
      const validationIssues = applyError instanceof ApolloError ? getGraphQLValidationIssues(applyError) : [];
      setMessages(validationIssues.length ? validationIssues.map(issue => `${issue.path}: ${issue.message}`) : [importErrorMessage]);
      if (!validationIssues.length) {
        reportError('Failed to import form JSON', applyError instanceof Error ? applyError : new Error('Form import failed'));
      }
    } finally {
      setIsApplying(false);
    }
  };
  return <DialogModal isOpen heading={mode === 'create' ? 'Create from JSON' : 'Update from JSON'} onClose={() => {
    if (!isApplying) onClose();
  }} showCloseButton={!isApplying} dialogPaperSx={{
    width: 720
  }} actions={<>
          <Button size="sm" variant="secondary" disabled={isApplying} onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" variant="primary" disabled={!json.trim() || isApplying || loading || !!error || !contract} onClick={apply}>
            {isApplying ? 'Applying JSON…' : 'Apply JSON'}
          </Button>
        </>}>
      <Stack spacing={2}>
        <Typography variant="body-s">
          Paste draft config only: extractionContext and form. Do not include a
          form name or description.
          {mode === 'update' && ' This replaces the draft in the editor. Review it, then select Save draft to persist it.'}
        </Typography>
        <Typography variant="body-xs">
          IDs are optional unless a conditional question references another
          question. Use a unique temporary ID for each referenced question.
          Platform replaces all IDs and preserves valid references.
        </Typography>
        {loading && <Typography variant="body-xs">Loading sample and schema…</Typography>}
        {error && <Alert severity="error" alertActionButton={{
        label: 'Try again',
        buttonProps: {
          onClick: async () => {
            await refetch();
          }
        }
      }}>
            Sample and schema could not be loaded.
          </Alert>}
        <Stack direction="row" spacing={1}>
          <Button size="sm" variant="secondary" disabled={!contract || loading || !!error || isApplying} onClick={() => {
          if (contract) copy(contract.sampleJson, () => showFeedback('Sample JSON copied to clipboard', {
            variant: 'success'
          }), () => showFeedback('Failed to copy sample JSON', {
            variant: 'error'
          }));
        }}>
            Copy sample JSON
          </Button>
          <Button size="sm" variant="secondary" disabled={!contract || loading || !!error || isApplying} onClick={() => {
          if (!contract) return;
          try {
            downloadJSON(contract.jsonSchema, `form-draft-schema-v${contract.schemaVersion}.json`);
          } catch (downloadError) {
            showFeedback('Failed to download JSON Schema', {
              variant: 'error'
            });
            reportError('Failed to download form schema', downloadError instanceof Error ? downloadError : new Error('Schema download failed'));
          }
        }}>
            Download JSON Schema
          </Button>
        </Stack>
        <TextInput id="form-import-json" label="Draft config JSON" multiline rows={12} value={json} disabled={isApplying} onChange={event => setJson(event.target.value)} />
        {messages.length > 0 && <Alert severity="error">
            <Stack spacing={0.5}>
              <Typography variant="title-xs">
                Fix these issues and try again. Your draft has not changed.
              </Typography>
              {messages.map((message, index) => <Typography key={`${index}-${message}`} variant="body-xs" sx={{
            overflowWrap: 'anywhere'
          }}>
                  {message}
                </Typography>)}
            </Stack>
          </Alert>}
      </Stack>
    </DialogModal>;
}